import React, { useState, useEffect } from 'react';
import { emailsAPI } from '../services/api';

// Palette Lexback (alignée sur Layout.js)
const GOLD = 'var(--brand-primary, #d9a847)';
const GOLD_TX = '#8a7234';
const GOLD_BG = 'rgba(217,168,71,0.14)';
const API_URL = process.env.REACT_APP_API_URL || 'https://lexback-production.up.railway.app';

function classify(typeDoc) {
  const s = (typeDoc || '').toLowerCase();
  if (s.includes('statut')) return 'Statuts';
  if (s.includes('pv') || s.includes('proc') || s.includes('age')) return 'PV';
  if (s.includes('kbis') || s.includes('extrait')) return 'Kbis';
  if (s.includes('rbe') || s.includes('bénéf') || s.includes('benef')) return 'RBE';
  if (s.includes('cession') || s.includes('parts')) return 'Cession';
  return 'Autres';
}

/**
 * Modal d'envoi de documents — deux modes :
 *   mode="client"     → envoi marque blanche au client (pièces + message)
 *   mode="signature"  → envoi pour signature (Yousign), choix des signataires
 *
 * Props : dossier, mode, onClose(), onSent(emailSend)
 */
export default function SendDocumentsModal({ dossier, mode: initialMode = 'client', onClose, onSent }) {
  const [mode, setMode] = useState(initialMode);
  const [docs, setDocs] = useState([]);
  const [selected, setSelected] = useState({});      // { [docId]: true }
  const [message, setMessage] = useState('');
  const [signers, setSigners] = useState([]);
  const [toEmail, setToEmail] = useState('');
  const [bccFormaliste, setBccFormaliste] = useState(true);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { setMode(initialMode); }, [initialMode]);

  useEffect(() => {
    if (!dossier) return;
    setLoading(true);
    const token = localStorage.getItem('lexback_token');
    fetch(`${API_URL}/api/documents/attachments/${dossier.id}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(r => (r.ok ? r.json() : {}))
      .then(grouped => {
        // l'endpoint renvoie un objet groupé par catégorie -> on aplatit
        const list = [];
        if (grouped && typeof grouped === 'object' && !grouped.detail) {
          Object.values(grouped).forEach(arr => { if (Array.isArray(arr)) arr.forEach(it => list.push(it)); });
        }
        setDocs(list);
        // pré-sélectionne Statuts + PV (cas le plus fréquent en signature)
        const pre = {};
        list.forEach(d => { const c = classify(d.subcategory); if (c === 'Statuts' || c === 'PV') pre[d.id] = true; });
        setSelected(pre);
      })
      .catch(() => setDocs([]))
      .finally(() => setLoading(false));

    setMessage("Bonjour,\n\nVeuillez trouver ci-joint les documents de votre dossier. Nous restons à votre disposition.");
    setToEmail(dossier.email_client_final || '');
    setBccFormaliste(true);
    setSigners([
      { name: dossier.societe_nom || 'Client', email: dossier.email_client_final || '', role: 'Client' },
    ]);
  }, [dossier]);

  if (!dossier) return null;

  const selectedIds = Object.keys(selected).filter(k => selected[k]);
  const selectedCount = selectedIds.length;
  const activeSigners = signers.filter(s => s.email.trim());

  const toggleDoc = (id) => setSelected(s => ({ ...s, [id]: !s[id] }));
  const updateSigner = (i, k, v) => setSigners(s => s.map((x, j) => j === i ? { ...x, [k]: v } : x));
  const addSigner = () => setSigners(s => [...s, { name: '', email: '', role: 'Signataire' }]);
  const removeSigner = (i) => setSigners(s => s.filter((_, j) => j !== i));

  const handleSend = async () => {
    setSending(true); setError(null);
    try {
      let res;
      if (mode === 'client') {
        res = await emailsAPI.sendClient(dossier.id, { document_ids: selectedIds, message, to_email: toEmail.trim(), bcc_formaliste: bccFormaliste && hasFormaliste });
      } else {
        res = await emailsAPI.sendSignature(dossier.id, {
          document_ids: selectedIds,
          signers: activeSigners.map(s => ({ name: s.name, email: s.email, role: s.role })),
        });
      }
      onSent && onSent(res.data);
      onClose && onClose();
    } catch (e) {
      setError(e.response?.data?.detail || "L'envoi a échoué.");
    } finally {
      setSending(false);
    }
  };

  const emailValid = /^\S+@\S+\.\S+$/.test((toEmail || '').trim());
  const hasFormaliste = !!(dossier.formaliste_id || dossier.formaliste_nom);
  const canSend = selectedCount > 0 && !sending &&
    (mode === 'client' ? emailValid : activeSigners.length > 0);

  const seg = (m, label, icon) => (
    <span onClick={() => setMode(m)} style={{
      cursor: 'pointer', padding: '6px 14px', fontSize: '12.5px', borderRadius: '7px', fontWeight: 500,
      color: mode === m ? GOLD : '#8b8576', background: mode === m ? '#1f1612' : 'transparent',
    }}>{icon} {label}</span>
  );

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(31,22,18,0.45)', zIndex: 200,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '18px',
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        width: '100%', maxWidth: '540px', maxHeight: '90vh', overflowY: 'auto',
        background: '#fff', borderRadius: '14px', fontFamily: 'var(--font-body, sans-serif)',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      }}>
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '0.5px solid rgba(0,0,0,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontWeight: 600, fontSize: '16px', color: '#1d1d1f' }}>
            Envoyer — {dossier.societe_nom || dossier.reference}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '20px', color: '#8b8576', cursor: 'pointer' }}>×</button>
        </div>

        <div style={{ padding: '14px 20px' }}>
          {/* Mode toggle */}
          <div style={{ display: 'flex', gap: '3px', background: '#f1efe8', borderRadius: '9px', padding: '3px', width: 'fit-content', marginBottom: '16px' }}>
            {seg('client', 'Au client', '✉')}
            {seg('signature', 'Pour signature', '✍')}
          </div>

          {/* Destinataire / signataires */}
          {mode === 'client' ? (
            <>
              <div style={{ fontSize: '10px', letterSpacing: '.04em', color: '#8b8576', marginBottom: '6px' }}>DESTINATAIRE</div>
              <input
                type="email"
                value={toEmail}
                onChange={e => setToEmail(e.target.value)}
                placeholder="email@client.com"
                style={{ width: '100%', boxSizing: 'border-box', border: `0.5px solid ${toEmail && !emailValid ? '#c0392b' : 'rgba(0,0,0,0.15)'}`, borderRadius: '8px', padding: '9px 11px', fontSize: '13px', color: '#1d1d1f', outline: 'none', fontFamily: 'inherit' }}
              />
              {!dossier.email_client_final && (
                <div style={{ fontSize: '11px', color: '#8b8576', marginTop: '4px' }}>Cet email sera enregistré sur le dossier pour les prochains envois.</div>
              )}
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px', fontSize: '12px', color: hasFormaliste ? '#424245' : '#aaa49a', cursor: hasFormaliste ? 'pointer' : 'default' }}>
                <input type="checkbox" checked={bccFormaliste && hasFormaliste} disabled={!hasFormaliste} onChange={e => setBccFormaliste(e.target.checked)} />
                Mettre {dossier.formaliste_nom ? dossier.formaliste_nom : 'la formaliste'} en copie cachée (invisible du client)
                {!hasFormaliste && <span style={{ fontStyle: 'italic' }}>· aucune formaliste assignée</span>}
              </label>
            </>
          ) : (
            <>
              <div style={{ fontSize: '10px', letterSpacing: '.04em', color: '#8b8576', marginBottom: '6px' }}>SIGNATAIRES</div>
              {signers.map((s, i) => (
                <div key={i} style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
                  <input value={s.name} onChange={e => updateSigner(i, 'name', e.target.value)} placeholder="Nom"
                    style={{ flex: 1, border: '0.5px solid rgba(0,0,0,0.12)', borderRadius: '7px', padding: '7px 9px', fontSize: '12.5px', outline: 'none' }} />
                  <input value={s.email} onChange={e => updateSigner(i, 'email', e.target.value)} placeholder="email@…"
                    style={{ flex: 1.4, border: '0.5px solid rgba(0,0,0,0.12)', borderRadius: '7px', padding: '7px 9px', fontSize: '12.5px', outline: 'none' }} />
                  {signers.length > 1 && (
                    <button onClick={() => removeSigner(i)} style={{ background: 'none', border: 'none', color: '#8b8576', cursor: 'pointer', fontSize: '16px' }}>×</button>
                  )}
                </div>
              ))}
              <span onClick={addSigner} style={{ fontSize: '12px', color: GOLD_TX, cursor: 'pointer' }}>+ Ajouter un signataire</span>
            </>
          )}

          {/* Documents */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '16px 0 6px' }}>
            <span style={{ fontSize: '10px', letterSpacing: '.04em', color: '#8b8576' }}>DOCUMENTS À JOINDRE</span>
            <span style={{ fontSize: '11px', color: GOLD_TX }}>{selectedCount} sélectionné{selectedCount > 1 ? 's' : ''}</span>
          </div>
          {loading ? (
            <div style={{ fontSize: '12.5px', color: '#8b8576', padding: '8px 0' }}>Chargement des pièces…</div>
          ) : docs.length === 0 ? (
            <div style={{ fontSize: '12.5px', color: '#aaa49a', fontStyle: 'italic', padding: '8px 0' }}>Aucun document sur ce dossier.</div>
          ) : docs.map(d => {
            const on = !!selected[d.id];
            return (
              <div key={d.id} onClick={() => toggleDoc(d.id)} style={{
                display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 11px',
                border: '0.5px solid rgba(0,0,0,0.1)', borderRadius: '8px', marginBottom: '6px', cursor: 'pointer',
                background: on ? 'rgba(217,168,71,0.05)' : '#fff',
              }}>
                <span style={{
                  width: '18px', height: '18px', borderRadius: '5px', flexShrink: 0,
                  border: `1.5px solid ${on ? GOLD : 'rgba(0,0,0,0.25)'}`, background: on ? GOLD : '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#1a1410', fontSize: '12px',
                }}>{on ? '✓' : ''}</span>
                <span style={{ flex: 1, fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.nom_original}</span>
                <span style={{ fontSize: '10.5px', background: GOLD_BG, color: GOLD_TX, padding: '1px 8px', borderRadius: '20px' }}>{d.subcategory || classify(d.subcategory)}</span>
              </div>
            );
          })}

          {/* Message (client only) */}
          {mode === 'client' && (
            <>
              <div style={{ fontSize: '10px', letterSpacing: '.04em', color: '#8b8576', margin: '16px 0 6px' }}>MESSAGE</div>
              <textarea value={message} onChange={e => setMessage(e.target.value)} rows={4}
                style={{ width: '100%', border: '0.5px solid rgba(0,0,0,0.12)', borderRadius: '8px', padding: '9px', fontSize: '12.5px', fontFamily: 'inherit', resize: 'vertical', outline: 'none', lineHeight: 1.5 }} />
            </>
          )}

          {/* Bandeau marque blanche / Yousign */}
          {mode === 'client' ? (
            <div style={{ display: 'flex', gap: '8px', background: 'rgba(217,168,71,0.07)', border: '0.5px solid rgba(217,168,71,0.3)', borderRadius: '8px', padding: '9px 11px', marginTop: '14px', fontSize: '11.5px', color: GOLD_TX }}>
              <span>🛡</span><div>Expédié au nom de votre cabinet. Les réponses du client reviennent vers l'email du cabinet.</div>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: '8px', background: '#EEEDFE', border: '0.5px solid #AFA9EC', borderRadius: '8px', padding: '9px 11px', marginTop: '14px', fontSize: '11.5px', color: '#3C3489' }}>
              <span>✍</span><div>Signature électronique via Yousign. Chaque signataire reçoit son lien ; le suivi remonte dans « Suivi des emails ».</div>
            </div>
          )}

          {error && <div style={{ marginTop: '12px', fontSize: '12px', color: '#c0392b' }}>{error}</div>}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 20px', borderTop: '0.5px solid rgba(0,0,0,0.06)', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button onClick={onClose} style={{ padding: '8px 16px', fontSize: '12.5px', color: '#8b8576', border: '0.5px solid rgba(0,0,0,0.12)', borderRadius: '8px', background: '#fff', cursor: 'pointer' }}>Annuler</button>
          <button onClick={handleSend} disabled={!canSend} style={{
            padding: '8px 16px', fontSize: '12.5px', fontWeight: 500, borderRadius: '8px', border: 'none',
            background: canSend ? GOLD : '#e7ddc6', color: canSend ? '#1a1410' : '#aaa49a', cursor: canSend ? 'pointer' : 'default',
          }}>
            {sending ? 'Envoi…' : mode === 'client'
              ? `Envoyer · ${selectedCount} pièce${selectedCount > 1 ? 's' : ''}`
              : `Envoyer en signature · ${activeSigners.length} signataire${activeSigners.length > 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}
